import React, { useState, useEffect, useRef } from 'react';
import { OperationBCI, OperationFormulaire } from '../types';
import '../../../composants/FormulaireAdministratif/FormulaireAdministratif.css';

// Descriptif administratif PROPRE a BDC operation (V2). Forke de l'ancien
// FormulaireAdministratif partage : BDC est toujours en mode BC (duree consultation
// fixe 10j), sans cahier des charges (rendu dans l'onglet technique), sans boutons
// de wizard. MS aura son propre composant lorsque son formulaire divergera.

interface IgnSuggestion {
  label: string;
  postcode?: string;
  city?: string;
}

interface OperationFormulaireAdminProps {
  operation: OperationBCI;
  formData: OperationFormulaire;
  viewMode: boolean;
  errors?: Record<string, string>;
  setFormData?: (data: OperationFormulaire) => void;
}

const toIso = (date: Date): string => date.toISOString().split('T')[0];

const OperationFormulaireAdmin: React.FC<OperationFormulaireAdminProps> = ({
  operation,
  formData,
  viewMode,
  errors = {},
  setFormData,
}) => {
  // Autocompletion d'adresse (IGN)
  const [adresseInput, setAdresseInput] = useState(formData.adresse || '');
  const [adresseSuggestions, setAdresseSuggestions] = useState<IgnSuggestion[]>([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSelectingRef = useRef(false);
  const hasUserTypedRef = useRef(false);
  const hasManuallyEditedDateRef = useRef(false);

  const update = (patch: Partial<OperationFormulaire>) => {
    if (setFormData) setFormData({ ...formData, ...patch });
  };

  // Suggestions IGN (uniquement apres saisie utilisateur, debounce 300ms, >= 3 car.)
  useEffect(() => {
    if (!hasUserTypedRef.current) {
      setAdresseSuggestions([]);
      return;
    }
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      return;
    }
    if (adresseInput.trim().length < 3) {
      setAdresseSuggestions([]);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await fetch(
          `https://data.geopf.fr/geocodage/completion?text=${encodeURIComponent(
            adresseInput,
          )}&index=StreetAddress&limit=5`,
        );
        if (response.ok) {
          const json = await response.json();
          const suggestions: IgnSuggestion[] = (json.results || []).map((item: any) => ({
            label: item.fulltext || '',
            postcode: item.zipcode || '',
            city: item.city || '',
          }));
          setAdresseSuggestions(suggestions);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des suggestions :', error);
        setAdresseSuggestions([]);
      }
    }, 300);
  }, [adresseInput]);

  // Synchronise l'input adresse quand formData.adresse change de l'exterieur.
  useEffect(() => {
    if (formData.adresse !== adresseInput) setAdresseInput(formData.adresse || '');
  }, [formData.adresse]);

  const handleAdresseInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasUserTypedRef.current = true;
    setAdresseInput(e.target.value);
    update({ adresse: e.target.value });
  };

  const handleSelectAdresse = (suggestion: IgnSuggestion) => {
    isSelectingRef.current = true;
    hasUserTypedRef.current = false;
    const adresse = suggestion.label.includes(',')
      ? suggestion.label.split(',')[0].trim()
      : suggestion.label;
    update({ adresse, codePostal: suggestion.postcode || '', ville: suggestion.city || '' });
    setAdresseInput(adresse);
    setAdresseSuggestions([]);
  };

  // Fin de consultation = debut + duree (10j pour BC), pour borner la date de debut d'operation.
  const startConsultation = formData.dateDebutConsultation
    ? new Date(formData.dateDebutConsultation)
    : new Date();
  const finConsultation = new Date(startConsultation);
  finConsultation.setDate(startConsultation.getDate() + Number(formData.delaisConsultation || 10));
  const finConsultationStr = toIso(finConsultation);

  // Recalcule la date de debut d'operation depuis la duree de consultation (sauf edition manuelle).
  useEffect(() => {
    if (!setFormData || viewMode || hasManuallyEditedDateRef.current) return;
    const delais = Number(formData.delaisConsultation || 10);
    const debut = new Date();
    debut.setDate(debut.getDate() + delais);
    const debutStr = toIso(debut);
    if (formData.dateDebutOperation !== debutStr) {
      setFormData({ ...formData, dateDebutOperation: debutStr });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.delaisConsultation, viewMode, setFormData]);

  // Recalcule la date de fin d'operation depuis debut + duree d'execution.
  useEffect(() => {
    if (!setFormData || viewMode || !formData.dateDebutOperation) return;
    const delais = Number(formData.delaisFinOperation || 10);
    const debut = new Date(formData.dateDebutOperation);
    const fin = new Date(debut);
    fin.setDate(debut.getDate() + delais);
    const finStr = toIso(fin);
    if (formData.dateFinOperation !== finStr) {
      setFormData({ ...formData, dateFinOperation: finStr });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.dateDebutOperation, formData.delaisFinOperation, viewMode, setFormData]);

  // --- Helpers d'affichage (lecture seule) ------------------------------------
  const op = operation.operation || operation;
  const visiteOui =
    operation.visite === true || operation.visite === 1 || operation.visite === '1';

  const dureeOperationLabel = (): string => {
    const dateDebut =
      operation.date_debut_operation || op.date_debut_operation || formData.dateDebutOperation;
    const dateFin = operation.date_fin_operation || op.date_fin_operation || formData.dateFinOperation;
    if (dateDebut && dateFin) {
      const debut = new Date(dateDebut);
      const fin = new Date(dateFin);
      if (!Number.isNaN(debut.getTime()) && !Number.isNaN(fin.getTime())) {
        const diffDays = Math.ceil((fin.getTime() - debut.getTime()) / (1000 * 60 * 60 * 24));
        return `${diffDays} jours`;
      }
    }
    if (formData.delaisFinOperation) return `${formData.delaisFinOperation} jours`;
    const apiDelai = operation.delai_execution || operation.delais || op.delai_execution || op.delais;
    if (apiDelai) return `${apiDelai} jours`;
    return '10 jours';
  };

  const dateFr = (value?: string): string => (value ? new Date(value).toLocaleDateString('fr-FR') : '');

  return (
    <div className={`step-container ${viewMode ? 'view-mode' : ''}`}>
      <div className="form-row grid-3">
        {/* Nom de l'operation */}
        <div className="form-group span-2">
          <label>Nom de l'opération {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
          {viewMode ? (
            <div className="input-inactif">{op.nom ?? operation.nom}</div>
          ) : (
            <>
              <input
                type="text"
                value={formData.nomOperation || ''}
                onChange={(e) => update({ nomOperation: e.target.value })}
                className={errors.nomOperation ? 'error' : ''}
                placeholder="Nom de l'opération"
              />
              {errors.nomOperation && <span className="error-message">{errors.nomOperation}</span>}
            </>
          )}
        </div>

        {/* Reference interne */}
        <div className="form-group">
          <label>N° de référence interne</label>
          {viewMode ? (
            <div className="input-inactif">{op.reference || operation.reference || '-'}</div>
          ) : (
            <>
              <input
                type="text"
                maxLength={100}
                value={formData.referenceInterne || ''}
                onChange={(e) => update({ referenceInterne: e.target.value })}
                className={errors.referenceInterne ? 'error' : ''}
                placeholder="N° de référence interne"
              />
              {errors.referenceInterne && <span className="error-message">{errors.referenceInterne}</span>}
            </>
          )}
        </div>

        {/* Adresse (autocomplete IGN) */}
        <div className="form-group">
          <label>Adresse de l'opération {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
          {viewMode ? (
            <div className="input-inactif">{op.adresse ?? operation.adresse ?? '-'}</div>
          ) : (
            <div className="autocomplete-container">
              <input
                type="text"
                id="adresse-autocomplete"
                value={adresseInput}
                onChange={handleAdresseInputChange}
                className={errors.adresse ? 'error' : ''}
                placeholder="Saisissez la rue de l'opération"
                autoComplete="off"
              />
              {adresseSuggestions.length > 0 && (
                <ul className="autocomplete-list" role="listbox">
                  {adresseSuggestions.map((suggestion, index) => (
                    <li key={index} role="option" onClick={() => handleSelectAdresse(suggestion)}>
                      {suggestion.label}
                    </li>
                  ))}
                </ul>
              )}
              {errors.adresse && <span className="error-message">{errors.adresse}</span>}
            </div>
          )}
        </div>

        {/* Code postal */}
        <div className="form-group">
          <label>Code postal {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
          {viewMode ? (
            <div className="input-inactif">{op.code_postal ?? operation.code_postal ?? '-'}</div>
          ) : (
            <>
              <input
                type="text"
                value={formData.codePostal || ''}
                onChange={(e) => update({ codePostal: e.target.value.replace(/\D/g, '').slice(0, 5) })}
                className={errors.codePostal ? 'error' : ''}
                placeholder="Code postal"
                inputMode="numeric"
                pattern="\d{5}"
                maxLength={5}
              />
              {errors.codePostal && <span className="error-message">{errors.codePostal}</span>}
            </>
          )}
        </div>

        {/* Ville */}
        <div className="form-group">
          <label>Ville {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
          {viewMode ? (
            <div className="input-inactif">{op.ville ?? operation.ville ?? '-'}</div>
          ) : (
            <>
              <input
                type="text"
                value={formData.ville || ''}
                onChange={(e) => update({ ville: e.target.value })}
                className={errors.ville ? 'error' : ''}
                placeholder="Ville"
              />
              {errors.ville && <span className="error-message">{errors.ville}</span>}
            </>
          )}
        </div>

        {/* Facturation suivie par (maitrise d'ouvrage) */}
        <div className="form-group">
          <label>
            Facturation suivie par : <span className="form-subtitle">(Si existant)</span>
          </label>
          {viewMode ? (
            <div className="input-inactif">
              {formData.coordonneesMaitriseDOuvrage ||
                operation.facturation ||
                operation.coordonnees_maitrise_ouvrage ||
                op.facturation ||
                op.coordonnees_maitrise_ouvrage ||
                '-'}
            </div>
          ) : (
            <input
              type="text"
              maxLength={255}
              value={formData.coordonneesMaitriseDOuvrage || ''}
              onChange={(e) => update({ coordonneesMaitriseDOuvrage: e.target.value })}
              placeholder="Affaire suivi par"
            />
          )}
        </div>

        {/* Coordonnees maitrise d'oeuvre */}
        <div className="form-group span-2">
          <label>
            Coordonnées de la maîtrise d'oeuvre <span className="form-subtitle">(Si existant)</span>
          </label>
          {viewMode ? (
            <div className="input-inactif">
              {formData.coordonneesMaitrise ||
                op.coordonnees_maitrise ||
                operation.coordonnees_maitrise ||
                '-'}
            </div>
          ) : (
            <input
              type="text"
              maxLength={255}
              value={formData.coordonneesMaitrise || ''}
              onChange={(e) => update({ coordonneesMaitrise: e.target.value })}
              placeholder="Coordonnées de la maîtrise d'oeuvre"
            />
          )}
        </div>

        {/* Duree de la consultation : fixe 10j pour les bons de commande */}
        <div className="form-group">
          <label>Durée de la consultation</label>
          {viewMode ? (
            <div className="input-inactif">
              10 jours (fixe - à partir du clic sur le lancement de la consultation)
            </div>
          ) : (
            <div className="input-inactif bc-fixed-delay">
              <span>10 jours (fixe pour les bons de commande)</span>
            </div>
          )}
        </div>

        {/* Duree d'execution de l'operation */}
        <div className="form-group">
          <label>
            Durée d'exécution de l'opération<span className="form-subtitle">(Minimum 10 jours)</span>
          </label>
          {viewMode ? (
            <div className="input-inactif">{dureeOperationLabel()}</div>
          ) : (
            <input
              type="number"
              min={10}
              value={formData.delaisFinOperation || 10}
              onChange={(e) => update({ delaisFinOperation: Math.max(10, Number(e.target.value)) })}
              placeholder="Nombre de jours"
            />
          )}
        </div>

        {/* Date de debut d'operation */}
        <div className="form-group">
          <label>Date prévisionnelle de début de l'opération</label>
          {viewMode ? (
            <div className="input-inactif">
              {dateFr(op.date_debut_operation || operation.date_debut_operation)}
            </div>
          ) : (
            <input
              type="date"
              min={finConsultationStr}
              value={formData.dateDebutOperation || ''}
              onChange={(e) => {
                hasManuallyEditedDateRef.current = true;
                update({ dateDebutOperation: e.target.value });
              }}
              className="date-input"
            />
          )}
        </div>

        {/* Date de fin d'operation (calculee, lecture seule) */}
        <div className="form-group">
          <label>Date prévisionnelle de fin de l'opération</label>
          <div className="input-inactif">
            {viewMode
              ? dateFr(
                  op.date_fin_operation || operation.date_fin_operation || formData.dateFinOperation,
                )
              : dateFr(formData.dateFinOperation)}
          </div>
        </div>

        {/* Obligation de visite prealable */}
        <div className="form-group">
          <label>Obligation de visiter préalablement à l'opération:</label>
          {viewMode ? (
            <div className="input-inactif">{visiteOui ? 'Oui' : 'Non'}</div>
          ) : (
            <div className="radio-options">
              <label>
                <input
                  type="radio"
                  name="visitePreavis"
                  checked={!formData.visitePreavis}
                  onChange={() => update({ visitePreavis: false, conditionVisite: '' })}
                />
                Non
              </label>
              <label>
                <input
                  type="radio"
                  name="visitePreavis"
                  checked={!!formData.visitePreavis}
                  onChange={() => update({ visitePreavis: true })}
                />
                Oui
              </label>
            </div>
          )}
        </div>

        {/* Condition de visite (si visite obligatoire) */}
        {(viewMode ? visiteOui : !!formData.visitePreavis) && (
          <div className="form-group span-2">
            <label>Condition de la visite {!viewMode && <span style={{ color: 'red' }}>*</span>}</label>
            {viewMode ? (
              <div className="input-inactif">
                {formData.conditionVisite || operation.condition_visite || 'Pas précisé'}
              </div>
            ) : (
              <>
                <input
                  type="text"
                  maxLength={255}
                  value={formData.conditionVisite || ''}
                  onChange={(e) => update({ conditionVisite: e.target.value })}
                  className={errors.conditionVisite ? 'error' : ''}
                  placeholder="Précisez les conditions de la visite"
                />
                {errors.conditionVisite && <span className="error-message">{errors.conditionVisite}</span>}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default OperationFormulaireAdmin;
